import { useMemo } from 'react';
import { BillRateBase, RoleRateApi } from 'api/rate-cards';
import {
  useRateCardVersionQuery,
  useRateCardVersionsQuery,
} from 'queries/rate-cards';

type RoleBillMap = {
  [roleId: string]: {
    roleEntry: RoleRateApi;
    currencyMap: {
      [currencyCode: string]: BillRateBase;
    };
  };
};

export const useProjectBillRates = (
  rateCardId: number | undefined,
  rateCardVersionId: number | undefined,
) => {
  const { data: rateCard, isFetching: isRateCardVersionLoading } =
    useRateCardVersionsQuery(rateCardId, {
      enabled: !!rateCardId,
    });

  const {
    data: projectRateCardVersion,
    isFetching: isProjectRateCardVersionLoading,
  } = useRateCardVersionQuery(rateCardId, rateCardVersionId, {
    enabled: !!(rateCardId && rateCardVersionId),
  });

  const [roleBillLookUp, defaultRate] = useMemo(() => {
    const rateCardVersion =
      projectRateCardVersion ||
      (rateCard?.rateCardVersions ?? []).find((card) => card.active);

    if (rateCardVersion?.roleBillRateMapping) {
      const roleBillMap = rateCardVersion?.roleBillRateMapping.reduce(
        (roleMap, roleEntry) => {
          return {
            ...roleMap,
            [roleEntry.roleId]: {
              ...roleEntry,
              currencyMap: roleEntry.multiCurrencyRates.reduce(
                (currMap, currencyEntry) => {
                  return {
                    ...currMap,
                    [currencyEntry.currency.currencyCode]: {
                      ...currencyEntry,
                    },
                  };
                },
                {},
              ),
            },
          };
        },
        {},
      ) as RoleBillMap;

      const rateDefault =
        rateCardVersion.defaultRate.multiCurrencyRates[0].billRate;

      return [roleBillMap, rateDefault];
    }

    return [null, null];
  }, [projectRateCardVersion, rateCard?.rateCardVersions]);

  return {
    isLoading: isRateCardVersionLoading || isProjectRateCardVersionLoading,
    roleBillLookUp,
    defaultRate,
  };
};
